/**
 * Entity spec sheets (doc 16 §2) and the shared state model.
 *
 * A spec is written BEFORE any pixel and is derived from the design docs, never
 * invented at art time. Footprint and stats come from `src/content/` — the art
 * never retypes a number, which is what `statDrift` exists to prove.
 */

import type { DepthPalette } from '../render/palette.ts';
import type { EntityCanvas, Sprite } from '../render/primitives.ts';

export type EntityClass = 'economy' | 'tower' | 'creature' | 'boss' | 'vfx' | 'ui';

export type EntityState =
  // shared
  | 'idle'
  | 'damaged'
  | 'destroyed'
  | 'hover'
  | 'selected'
  | 'ghost'
  /** Rising out of the seabed after placement — a derived transform, not authored. */
  | 'building'
  // economy
  | 'working'
  | 'starved'
  // tower
  | 'tracking'
  | 'firing'
  | 'dry';

export const ECONOMY_STATES: EntityState[] = [
  'idle',
  'working',
  'starved',
  'damaged',
  'destroyed',
  'hover',
  'selected',
  'ghost',
];

export const TOWER_STATES: EntityState[] = [
  'idle',
  'tracking',
  'firing',
  'dry',
  'damaged',
  'destroyed',
  'hover',
  'selected',
  'ghost',
];

export interface BuildContext {
  depth: DepthPalette;
  state: EntityState;
  /** Animation phase, 0..1, for cyclic states. Quantized by the caller. */
  phase: number;
  /** Facing 0..7: 0=E, 1=SE, 2=S, 3=SW, 4=W, 5=NW, 6=N, 7=NE. */
  facing: number;
  /**
   * Tiers bought per upgrade path (doc 05's 3×3). Towers wear their upgrades:
   * each path owns a motif that appears at T1, grows at T2, and changes the
   * silhouette at T3 (doc 16 §1's capstone variant requirement).
   */
  upgrades?: Tiers;
}

export interface Tiers {
  a: number;
  b: number;
  c: number;
}

export const NO_TIERS: Tiers = { a: 0, b: 0, c: 0 };

/** Upgrade tiers for a context, defaulted — most callers draw a base entity. */
export function tiersOf(ctx: BuildContext): Tiers {
  return ctx.upgrades ?? NO_TIERS;
}

/** Draws the entity's own volumes. Outline and state transforms are applied by `buildSprite`. */
export type EntityDraw = (c: EntityCanvas, ctx: BuildContext) => void;

export interface EntitySpec {
  id: string;
  class: EntityClass;
  /** Display name, from the design docs. */
  name: string;
  /** Square footprint in tiles, taken from `src/content/`. */
  size: number;
  /** Declared volume height above the anchor, in internal px (Law L3). */
  height: number;
  tall: boolean;
  /** Must be unique within the class (doc 10 rule 6). */
  silhouetteFamily: string;
  signature: { colour: string; motif: string };
  /**
   * Colours this entity spends outside the shared ramps, declared up front.
   * Law L5 is a budget, not a ban — but every colour outside the palette has to
   * be justified in writing, here, where a reviewer sees it.
   */
  detailColours?: string[];
  states: EntityState[];
  /** Where the numbers came from, for `statDrift`. */
  content: { file: 'buildings' | 'towers'; key: string };
  /** Stamped at Pass F, after stakeholder sign-off. */
  reviewedAt: string | null;
  draw: EntityDraw;
}

/** Screen offsets for the 8 facings, preserving the 2:1 iso ratio. */
export const FACING_VECTORS: ReadonlyArray<{ x: number; y: number }> = [
  { x: 4, y: 0 }, // 0 E
  { x: 2, y: 1 }, // 1 SE
  { x: 0, y: 2 }, // 2 S
  { x: -2, y: 1 }, // 3 SW
  { x: -4, y: 0 }, // 4 W
  { x: -2, y: -1 }, // 5 NW
  { x: 0, y: -2 }, // 6 N
  { x: 2, y: -1 }, // 7 NE
];

/**
 * Per-facing divisors that turn `length` into an offset of roughly constant
 * APPARENT length on screen.
 *
 * True iso foreshortening would make an east-pointing barrel twice the screen
 * length of a south-pointing one — geometrically correct, and unreadable: half
 * the facings would look like stubs. Every isometric game cheats here; ours
 * cheats by rule, in one place, so all eight facings of all six towers cheat
 * identically. The 2:1 slope is never violated, only the magnitude.
 */
const FACING_DIVISOR = [4, 2.6, 3.0, 2.6, 4, 2.6, 3.0, 2.6];

/** Facing offset at a given apparent length, snapped to whole pixels (Law L4). */
export function facingOffset(facing: number, length: number): { x: number; y: number } {
  const i = ((facing % 8) + 8) % 8;
  const v = FACING_VECTORS[i]!;
  const scale = length / FACING_DIVISOR[i]!;
  return { x: Math.round(v.x * scale), y: Math.round(v.y * scale) };
}
